import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const asArray = (value) => (Array.isArray(value) ? value : []);

const DrugDetails = () => {
  const navigate = useNavigate();
  const { drugId } = useParams();
  const { loading, error, data, execute } = useAPICall();

  const details = useMemo(() => data?.data || data || {}, [data]);
  const sourceRecords = useMemo(() => asArray(details.srcRecord || details.sourceRecords || details.records), [details]);

  useEffect(() => {
    const load = async () => {
      try {
        await execute(API_CONFIG.drug.details, null, {
          params: { batchId: drugId },
          errorMessage: 'Unable to load drug details.',
        });
      } catch {
      }
    };

    load();
  }, [drugId, execute]);

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <PageBreadcrumbs items={[
          { label: 'Drug Search', path: '/search/drug' },
          { label: 'Drug Details' }
        ]} />
      </Box>
      <Typography sx={{ fontWeight: 600,  color: '#1e293b', mb: 0.25 }}>Drug Details</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Record details for Drug ID: {drugId}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button className="action-btn process-btn" variant="outlined" onClick={() => navigate('/approve-ndc')}>
          Open Approve NDC
        </Button>
      </Box>
      {loading ? (
        <Paper sx={{ p: 2, mb: 1.5 }}>
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="text" width="80%" height={24} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 1.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Drug ID:</strong> {details.id || details.drug_id || drugId}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Name:</strong> {details.name || details.drug_name || details.brand_name || '-'}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>NDC:</strong> {details.ndc || '-'}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Manufacturer:</strong> {details.manufacturer || '-'}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Route:</strong> {details.route || '-'}</Typography></Grid>
              <Grid item xs={12} md={4}><Typography variant="body2"><strong>Status:</strong> {details.status || details.recordStatus || '-'}</Typography></Grid>
            </Grid>
          </Paper>

          <GlobalMaterialTable
            columns={[
              { header: 'Source', id: 'source', accessorFn: (row) => row.source || row.system || '-' },
              { header: 'Field', id: 'field', accessorFn: (row) => row.field || row.fieldName || '-' },
              { header: 'Value', id: 'value', accessorFn: (row) => row.value || row.fieldValue || '-' },
            ]}
            data={sourceRecords}
            emptyMessage="No source records found"
            enablePagination={false}
            enableBottomToolbar={false}
            muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 2 } }}
          />
        </>
      )}
    </Box>
  );
};

export default DrugDetails;
